import { cn } from '@ifixit/ui/lib/utils';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { FaIcon } from '@ifixit/ui/components/icon';
import { _js } from '@ifixit/localize';
import { ReactNode, useContext, MouseEventHandler } from 'react';
import { GuideSubscriptionContext, withGuideSubscription } from './GuideSubscription';
import { GuideFavoriteContext, withGuideFavorite } from './GuideFavorite';
import { createLazyComponent, preloadComponent } from 'Shared/utils/lazy-loading';

const importGuideEmbedModal = () => import('./GuideEmbedModal');
const GuideEmbedModal = createLazyComponent(importGuideEmbedModal);
import { useExpandCollapseComments } from './GuideComments';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import copyToClipboard from 'Shared/copy_to_clipboard';
import { SharedNavBar, BreadcrumbData } from 'Guide/PageNavigation/SharedNavBar';

export interface NavData {
   breadCrumbs: Array<BreadcrumbData>;
   answersPartsUrl: string;
   devicePartsUrl?: string | null;
   fullScreenUrl: string;
   guidesUrl: string;
   sharableUrl: string;
   historyUrl?: string;
   pdfUrl?: string;
   translateUrl?: string;
   editUrl?: string;
   editTranslateUrl?: string;
   isTeardown: boolean;
   showExpandComments?: true;
   problemListUrl?: string;
}

export function GuideNav(props: NavData) {
   const { breadCrumbs, answersPartsUrl, devicePartsUrl, guidesUrl, problemListUrl } = props;
   const showParts = devicePartsUrl !== undefined;
   const navTabData = showParts ? [{ name: _js('Parts'), url: devicePartsUrl ?? undefined }] : [];
   return (
      <SharedNavBar
         breadCrumbs={breadCrumbs}
         navTabs={[
            ...navTabData,
            { name: _js('Guides'), url: guidesUrl, isCurrentPage: true },
            problemListUrl
               ? { name: _js('Troubleshooting'), url: problemListUrl }
               : { name: _js('Answers'), url: answersPartsUrl },
         ]}
         editMenuComponent={<EditMenu {...props} />}
      />
   );
}

type StyledMenuItemProps = {
   isLink?: boolean;
   href?: string;
   icon?: string;
   iconStyle?: 'solid' | 'regular';
   className?: string;
   closeOnSelect?: boolean;
   onClick?: MouseEventHandler<HTMLElement>;
   onMouseEnter?: MouseEventHandler<HTMLElement>;
   children?: ReactNode;
};

const menuItemClass =
   'text-sm text-gray-900 visited:text-gray-900 hover:no-underline cursor-pointer';

function StyledMenuItem({
   isLink,
   href,
   icon,
   iconStyle = 'regular',
   className,
   closeOnSelect,
   onClick,
   onMouseEnter,
   children,
}: StyledMenuItemProps) {
   // If link but no href, bail
   if (isLink && !href) {
      return null;
   }

   const itemIcon = icon ? (
      <FaIcon name={icon} style={iconStyle} className="size-4 max-w-4 shrink-0 text-gray-600" />
   ) : undefined;
   const onSelect = closeOnSelect === false ? (event: Event) => event.preventDefault() : undefined;

   if (isLink) {
      return (
         <DropdownMenuItem asChild onSelect={onSelect} className={cn(menuItemClass, className)}>
            <a href={href} onClick={onClick} onMouseEnter={onMouseEnter}>
               {itemIcon}
               {children}
            </a>
         </DropdownMenuItem>
      );
   }

   return (
      <DropdownMenuItem
         onSelect={onSelect}
         onClick={onClick}
         onMouseEnter={onMouseEnter}
         className={cn(menuItemClass, className)}
      >
         {itemIcon}
         {children}
      </DropdownMenuItem>
   );
}

function EditMenu({
   fullScreenUrl,
   sharableUrl,
   historyUrl,
   pdfUrl,
   translateUrl,
   editUrl,
   editTranslateUrl,
   showExpandComments,
   isTeardown,
}: NavData & { isTeardown: boolean }) {
   const guideSubscriptionContext = useContext(GuideSubscriptionContext);
   const guideFavoriteContext = useContext(GuideFavoriteContext);
   const { isOpen: isEmbedOpen, onOpen: onEmbedOpen, onClose: onEmbedClose } = useDisclosure();
   const [isExpanded, toggleExpandComments] = useExpandCollapseComments();

   return (
      <div className="flex">
         {(editUrl || editTranslateUrl) && (
            <a
               href={editUrl || editTranslateUrl}
               id="edit-nav-link"
               className="inline-flex items-center justify-center gap-2 h-full px-4 rounded-none border-l border-gray-300 shrink-0 font-semibold text-brand-500 visited:text-brand-500 hover:no-underline hover:bg-gray-100"
            >
               <FaIcon name="pen-to-square" style="regular" className="size-4 text-brand-500" />
               <span>{editUrl ? _js('Edit') : _js('Translate')}</span>
            </a>
         )}
         <DropdownMenu modal={false}>
            <DropdownMenuTrigger
               className="guide-options-dropdown inline-flex items-center justify-center h-full px-4 rounded-none border-x border-y-0 border-gray-300 bg-transparent hover:bg-gray-100 shrink-0 cursor-pointer"
               aria-label={_js('Guide Options Menu')}
            >
               <FaIcon name="angle-down" style="regular" className="size-4 text-brand-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="guide-options-dropdown-list z-[3]" align="end">
               <StyledMenuItem
                  icon="arrows-maximize"
                  isLink
                  href={fullScreenUrl}
                  className="fullscreen-nav-link"
               >
                  {_js('Full screen')}
               </StyledMenuItem>
               <StyledMenuItem
                  icon="clock-rotate-left"
                  isLink
                  href={historyUrl}
                  className="link-history"
               >
                  {_js('History')}
               </StyledMenuItem>
               {isTeardown && (
                  <StyledMenuItem
                     icon="circle-star"
                     iconStyle={guideFavoriteContext.isFavorite ? 'solid' : 'regular'}
                     onClick={() => {
                        if (guideFavoriteContext.isFavorite) {
                           guideFavoriteContext.unfavorite(App.guideid, App.langid, isTeardown);
                        } else {
                           guideFavoriteContext.favorite(App.guideid, App.langid, isTeardown);
                        }
                     }}
                     closeOnSelect={false}
                  >
                     {guideFavoriteContext.isFavorite
                        ? _js('Remove from Favorites')
                        : _js('Save to Favorites')}
                  </StyledMenuItem>
               )}
               <StyledMenuItem icon="download" isLink href={pdfUrl}>
                  {_js('Download PDF')}
               </StyledMenuItem>
               <StyledMenuItem icon="language" isLink href={translateUrl}>
                  {_js('Translate')}
               </StyledMenuItem>
               {showExpandComments && (
                  <StyledMenuItem
                     icon={isExpanded ? 'message-minus' : 'message-plus'}
                     onClick={toggleExpandComments}
                     className="expand-comments"
                     closeOnSelect={false}
                  >
                     {isExpanded ? _js('Collapse Comments') : _js('Expand Comments')}
                  </StyledMenuItem>
               )}
               <CopyableStyledMenuItem
                  icon="link-simple"
                  className="show-shareable-link"
                  closeOnSelect={false}
                  copyText={sharableUrl}
               >
                  {_js('Get shareable link')}
               </CopyableStyledMenuItem>
               <StyledMenuItem
                  icon="code"
                  onClick={onEmbedOpen}
                  onMouseEnter={() => preloadComponent(importGuideEmbedModal)}
                  className="show-embed"
               >
                  {_js('Embed this guide')}
               </StyledMenuItem>
               <StyledMenuItem
                  icon={guideSubscriptionContext.isSubscribed ? 'bell-slash' : 'bell'}
                  onClick={() => {
                     if (guideSubscriptionContext.isSubscribed) {
                        guideSubscriptionContext.unsubscribe(App.guideid, App.langid);
                     } else {
                        guideSubscriptionContext.subscribe(App.guideid, App.langid);
                     }
                  }}
                  closeOnSelect={false}
               >
                  {guideSubscriptionContext.isSubscribed
                     ? _js('Stop Notifications')
                     : _js('Notify me on changes')}
               </StyledMenuItem>
            </DropdownMenuContent>
         </DropdownMenu>
         {isEmbedOpen && (
            <GuideEmbedModal isOpen={isEmbedOpen} onClose={onEmbedClose} guideid={App.guideid} />
         )}
      </div>
   );
}

function CopyableStyledMenuItem({
   children,
   hideAfterMs = 1000,
   copyText,
   ...props
}: {
   hideAfterMs?: number;
   copyText: string;
   children: ReactNode;
} & StyledMenuItemProps) {
   const disclosureProps = useDisclosure();

   const onClick: MouseEventHandler<HTMLElement> = () => {
      disclosureProps.onOpen();
      copyToClipboard(copyText);
      setTimeout(disclosureProps.onClose, hideAfterMs);
   };

   const icon = disclosureProps.isOpen ? 'check' : props.icon;

   return (
      <StyledMenuItem {...props} icon={icon} onClick={onClick}>
         <Tooltip open={disclosureProps.isOpen}>
            <TooltipTrigger asChild>
               <span>{children}</span>
            </TooltipTrigger>
            <TooltipContent className="text-sm">{_js('Copied!')}</TooltipContent>
         </Tooltip>
      </StyledMenuItem>
   );
}

initializeChakraComponent('GuideNavComponent', withGuideSubscription(withGuideFavorite(GuideNav)));
